import { Component, OnInit, TemplateRef, ViewChild, computed, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { SearchableSelectComponent } from "src/app/components/searchable-select/searchable-select.component"
import { SearchableSelectOption } from "src/app/models"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatSelectModule } from "@angular/material/select"
import { MatIcon } from "@angular/material/icon"
import { EmployeeService, VdkCardService } from "src/app/services"
import { ToastService } from "src/app/services"
import { VdkMbrads, EmployeeMbrad } from "src/app/models"
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog"
import { catchError, finalize, map, throwError } from "rxjs"
@Component({
    selector: "valben-vdk",
    standalone: true,
    imports: [
        SearchableSelectComponent,
        FormsModule,
        NgClass,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatIcon,
        MatDialogModule,
    ],
    templateUrl: "./vdk.page.html",
    styles: [`
        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
    `],
})
export class VdkPage implements OnInit {
    selectedEmployees = signal<readonly EmployeeMbrad[]>([]);
    mbradTypes = ["HRPRO", "HEFA"];
    textMbrads: string = "";
    selectedEmployeesTemp: EmployeeMbrad[] = [];
    confirmAgain: boolean = false;
    requestCalled: boolean = true;

    loading = signal<boolean>(false);
    employees = signal<EmployeeMbrad[]>([]);
    selectedMbradType = signal(this.mbradTypes[0]);
    otherTypeErr = signal("");

    // The dropdown shows "fullName (mbrad)" but the trigger shows just the mbrad, so the label stays short
    // and searchText covers both fields (what filterBy="fullName,mbrad" used to do).
    employeeOptions = computed<SearchableSelectOption<EmployeeMbrad, EmployeeMbrad>[]>(() =>
        this.employees().map(employee => ({
            value: employee,
            label: employee.mbrad,
            searchText: `${employee.fullName} ${employee.mbrad}`,
            data: employee,
        }))
    );

    @ViewChild("textDialog") private _textDialog!: TemplateRef<unknown>;
    private readonly _dialog = inject(MatDialog);
    private _textRef: MatDialogRef<unknown> | null = null;

    constructor(
        private _employeeService: EmployeeService,
        private _vdkService: VdkCardService,
        private _messageService: ToastService,
    ) {}

    selectMbradType(event: any): void {
        if (event.value === this.selectedMbradType()) return;
        this.selectedMbradType.set(event.value);
        this.selectedEmployees.set([]);
        this._getEmployees();
    }

    generate(): void {
        this.loading.set(true);
        const vdkMbrads: VdkMbrads = {
            mbradType: this.selectedMbradType(),
            mbrads: this.selectedEmployees().map(e => e.mbrad),
        };
        this._vdkService.generate(vdkMbrads).pipe(
            finalize(() => this.loading.set(false)),
            catchError(err => {
                this._messageService.add({
                    severity: "error",
                    summary: "Greška",
                    detail: err.status === 400 && err.error.message
                        ? err.error.message
                        : "Došlo je do pogreške prilikom obrade kartica."
                });
                return throwError(() => err);
            }),
        ).subscribe(() => {
            this.selectedEmployees.set([]);
            this._messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Započeta je obrada digitalnih kartica zaposlenika.",
            });
        });
    }

    ngOnInit(): void {
        this._getEmployees();
    }

    openText(): void {
        this._textRef = this._dialog.open(this._textDialog, { width: "50rem" })
    }

    confirmText(): void {
        if (this.confirmAgain) {
            this.confirmAgain = false
            this.textMbrads = ""
            this.otherTypeErr.set("")
            this.selectedEmployees.set(this.selectedEmployeesTemp)
            this._textRef?.close()
            return
        }

        const mbrads = [...new Set(
            this.textMbrads.split(",").map(x => x.trim()).filter(x => x.length > 0)
        )]
        const allDigits = mbrads.filter(x => /^\d+$/.test(x) === false)
        if (allDigits.length > 0) {
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: `Mbradovi nisu ispravni: ${allDigits.join(", ")}`,
                life: 10000,
            })
            return
        }

        this.selectedEmployeesTemp = []
        const err = []
        for (let mbrad of mbrads) {
            const emp = this.employees().find(x => x.mbrad === mbrad)
            if (emp) {
                this.selectedEmployeesTemp.push(emp)
            } else {
                err.push(mbrad)
            }
        }

        if (err.length > 0) {
            this.otherTypeErr.set(err.join(", "))
            if (this.selectedEmployeesTemp.length > 0) {
                this.confirmAgain = true
            }
            return
        }

        this.textMbrads = ""
        this.otherTypeErr.set("")
        this.selectedEmployees.set(this.selectedEmployeesTemp)
        this._textRef?.close()
    }

    private _getEmployees(): void {
        this.loading.set(true);
        this._employeeService.employees$
            .pipe(
                map(employees => employees.filter(e => e.mbradType === this.selectedMbradType())),
                finalize(() => this.loading.set(false)),
            )
            .subscribe({
                next: employees => this.employees.set(employees),
                error: err => {
                    this.loading.set(false)
                    this._messageService.add({
                        severity: "error",
                        summary: "Greška",
                        detail: err.status === 400 && err.error.message
                            ? err.error.message
                            : "Došlo je do pogreške prilikom obrade kartica."
                    })
                }
            })
    }
}
